import {
  guestIsBlank,
  guestQuestionDefault,
  guestRowErrors,
  guestRowsForTickets,
  withGuestDefaults,
  withGuestName,
} from '@repo/lib';
import { isEqual } from 'lodash';
import { formatFieldValue, getAddressRule } from '../addressRules';

const idOf = value => String(value?._id ?? value ?? '');

export function checkoutGuestSettings(checkout) {
  const settings = checkout?.eventDetails?.attendeeDetails;
  if (!settings?.enabled || settings.when === 'afterBooking') return null;
  return {
    requireAllBeforePayment: settings.requireAllBeforePayment !== false,
    emailDomains: settings.emailDomains || 'any',
  };
}

// The booker's email follows the domain rule in both modes; only attendees wait until after booking.
export function checkoutBookerDomainRule(checkout) {
  const settings = checkout?.eventDetails?.attendeeDetails;
  return settings?.enabled ? settings.emailDomains || 'any' : null;
}

export function orderedTicketTypes(checkout) {
  return (checkout?.ticketGroups ?? [])
    .filter(group => group.status === 'enabled')
    .flatMap(group => group.ticketTypes ?? []);
}

export function ticketQuantities(formTicketGroups = {}) {
  const quantities = {};
  Object.values(formTicketGroups ?? {}).forEach(entries =>
    (entries ?? []).forEach(({ ticketTypeId, quantity }) => {
      const id = idOf(ticketTypeId);
      quantities[id] = (quantities[id] ?? 0) + (Number(quantity) || 0);
    }),
  );
  return quantities;
}

const blankRow = ticketType => ({ ticketType, name: '', email: '', answers: [], done: false });

/** One guest row per selected ticket, keeping what was typed in rows that stay. */
export function currentGuestRows(checkout, form) {
  return guestRowsForTickets(
    orderedTicketTypes(checkout).map(type => idOf(type._id)),
    ticketQuantities(form?.ticketGroups),
    form?.guests ?? [],
    blankRow,
  );
}

export function answerText(row, question) {
  return (row.answers ?? []).find(a => a.fieldId === idOf(question._id))?.value ?? '';
}

/** A question's answer on a row: the name and email live on the row itself. */
export function rowValue(row, question) {
  if (question.type === 'name') return row.name;
  if (question.type === 'email') return row.email;
  const answer =
    (row.answers ?? []).find(a => a.fieldId === idOf(question._id)) ??
    guestQuestionDefault(question);
  if (question.element === 'address') return answer?.meta?.address;
  // Stored as text, but the checkbox reads any text, 'false' included, as ticked.
  if (question.element === 'checkbox') return String(answer?.value) === 'true';
  return answer ? (answer.values ?? answer.value) : undefined;
}

// The inputs always carry a country, so an untouched address only counts once something is typed.
const addressHasText = address =>
  [address?.billing, address?.shipping].some(part =>
    Object.entries(part ?? {}).some(
      ([key, text]) => key !== 'country' && String(text ?? '').trim(),
    ),
  );

export function withRowValue(row, question, value) {
  if (question.type === 'name') return { ...row, name: value ?? '' };
  if (question.type === 'email') return { ...row, email: value ?? '' };
  const fieldId = idOf(question._id);
  const answer =
    question.element === 'address' && typeof value === 'string'
      ? { fieldId, value }
      : question.element === 'address'
        ? {
            fieldId,
            value: addressHasText(value) ? formatFieldValue(value) : '',
            meta: { address: value },
          }
        : Array.isArray(value)
          ? { fieldId, values: value.map(String) }
          : { fieldId, value: value == null ? '' : String(value) };
  return { ...row, answers: [...(row.answers ?? []).filter(a => a.fieldId !== fieldId), answer] };
}

/** A row in the shape the shared group registration rules and the server read. */
export function rowAsGuest(row) {
  return {
    ticketType: row.ticketType,
    name: row.name,
    email: row.email,
    answers: row.answers ?? [],
  };
}

export function rowIsBlank(row) {
  return guestIsBlank(rowAsGuest(row));
}

/** What Save attendee last kept on a row: edits after it only count once saved again. */
export function savedGuest(row) {
  return row.saved ?? rowAsGuest(row);
}

const hasText = value => String(value ?? '').trim() !== '';
const comparable = guest => ({
  name: (guest.name ?? '').trim(),
  email: (guest.email ?? '').trim(),
  answers: Object.fromEntries(
    (guest.answers ?? [])
      .filter(answer => hasText(answer.value) || (answer.values ?? []).some(hasText))
      .map(answer => [answer.fieldId, answer.values ?? answer.value]),
  ),
});

/** Typed into and not saved: a new row with anything in it, or a saved row changed since. */
export function rowHasUnsavedChanges(row) {
  if (!row.done) return !rowIsBlank(row);
  return !isEqual(comparable(rowAsGuest(row)), comparable(savedGuest(row)));
}

/** The row Pay waits on: the open row while it edits a saved one or holds typing, else any row with unsaved typing. */
export function pendingGuestIndex(rows, openIndex = -1) {
  const open = rows[openIndex];
  if (open && (open.done || rowHasUnsavedChanges(open))) return openIndex;
  return rows.findIndex(rowHasUnsavedChanges);
}

/** Cancel on an open row: back to what was last saved, or blank if it never was. */
export function cancelledRow(row) {
  if (!row.done) return blankRow(row.ticketType);
  const { ticketType: _ticketType, ...saved } = savedGuest(row);
  return { ...row, ...saved };
}

export const PARTIAL_ADDRESS_ERROR = 'Enter the full address.';

const addressIsPartial = address =>
  [address?.shipping ?? address?.billing, address?.sameAsShipping === false && address?.billing]
    .filter(Boolean)
    .some(part => getAddressRule(part.country).required.some(key => !part[key]?.trim()));

/** Why a row can't be saved yet, checked against the rows already saved. */
export function rowErrors({ rows, index, questions, bookerEmail, emailDomains }) {
  const guests = rows.map((row, at) =>
    at === index
      ? withGuestDefaults(questions, rowAsGuest(row))
      : row.done
        ? withGuestDefaults(questions, savedGuest(row))
        : {},
  );
  const { errors } = guestRowErrors({ questions, guests, index, bookerEmail, emailDomains });
  questions
    .filter(question => question.element === 'address' && question.required)
    .forEach(question => {
      const id = idOf(question._id);
      if (errors.answers?.[id]) return;
      if (addressIsPartial(rowValue(rows[index], question))) {
        errors.answers = { ...errors.answers, [id]: PARTIAL_ADDRESS_ERROR };
      }
    });
  return errors;
}

/** Saved rows go to the server; rows still open or untouched go as blanks. */
export function guestsForPayment(rows) {
  return rows.map(row => (row.done ? savedGuest(row) : { ticketType: row.ticketType }));
}

export function bookerEmailOf(formFields = {}) {
  const field = Object.values(formFields ?? {}).find(f => f?.type === 'email');
  return typeof field?.value === 'string' ? field.value.trim() : '';
}

export function bookerNameOf(formFields = {}) {
  const fields = Object.values(formFields ?? {});
  const valueOf = type => {
    const value = fields.find(f => f?.type === type)?.value;
    return typeof value === 'string' ? value.trim() : '';
  };
  return valueOf('name') || [valueOf('first-name'), valueOf('last-name')].filter(Boolean).join(' ');
}

/** The guests a recovery link brings back: the ones saved before leaving count as saved again. */
export function draftGuestRows(draftCheckout) {
  const guests = draftCheckout?.guests;
  if (!Array.isArray(guests) || !guests.length) return undefined;
  return guests.map(guest => {
    const row = {
      ticketType: idOf(guest.ticketType),
      name: guest.name ?? '',
      email: guest.email ?? '',
      answers: guest.answers ?? [],
    };
    return guestIsBlank(row) ? { ...row, done: false } : { ...row, done: true, saved: row };
  });
}

/** What the abandonment draft keeps of the guests: saved ones in full, the rest blank. */
export function draftPaymentGuests(checkout, form) {
  if (!checkoutGuestSettings(checkout)) return undefined;
  const rows = currentGuestRows(checkout, form);
  return rows.some(row => row.done) ? guestsForPayment(rows) : undefined;
}

/** A row saved with Save attendee: done, and named from First and Last name when Name isn't asked. */
export function savedRow(row, questions) {
  const named = withGuestName(questions, row);
  return { ...named, done: true, saved: rowAsGuest(named) };
}

/** Guest 1 as the booker: their name goes to Name, or split across First and Last name. */
export function rowAsBooker(row, questions, formFields = {}) {
  const fields = Object.values(formFields ?? {});
  const valueOf = type => {
    const value = fields.find(f => f?.type === type)?.value;
    return typeof value === 'string' ? value.trim() : '';
  };
  const name = bookerNameOf(formFields);
  const filled = { ...row, name, email: bookerEmailOf(formFields) };
  if (questions.some(question => question.type === 'name')) return filled;
  const [given = '', ...rest] = name.split(/\s+/).filter(Boolean);
  const parts = {
    'first-name': valueOf('first-name') || given,
    'last-name': valueOf('last-name') || rest.join(' '),
  };
  return questions
    .filter(question => question.type in parts)
    .reduce((next, question) => withRowValue(next, question, parts[question.type]), filled);
}
